// "البطاقات التقنية" — each training's technical data sheet (fiche technique)
// and the social media design made from it. Sales upload the sheet and so ask
// the designer for a design; the designer picks it up, uploads the design and
// delivers it; sales can send it back for changes. One request per training.
//
// Who may write which half is in src/lib/roles.ts, the status moves in
// src/lib/fiches.ts; both are enforced again by `fiches.*` and the upload
// route — the hidden buttons here are convenience.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	redirect,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router";
import {
	CalendarClock,
	Check,
	Download,
	ExternalLink,
	FileImage,
	FileText,
	Loader2,
	Trash2,
	Upload,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { GhostButton, Modal, PrimaryButton } from "#/components/admin/modal";
import { TabBar, type TabItem } from "#/components/admin/tabs";
import { authClient } from "#/lib/auth-client";
import { editionLabel, isoDay } from "#/lib/editions";
import {
	FICHE_ACCEPT_ATTR,
	FICHE_STATUS_LABELS,
	type FicheFileKind,
	type FicheStatus,
	ficheActions,
	isPreviewable,
	validateFicheUpload,
	waitsOn,
} from "#/lib/fiches";
import { algiersToday } from "#/lib/lead-filters";
import {
	canEditFicheDesign,
	canEditFicheSheet,
	canViewFiches,
	homeRouteFor,
	isDesigner,
} from "#/lib/roles";
import { getSession } from "#/lib/session";
import { formatBytes } from "#/lib/uploads";
import { dayLabel } from "#/lib/weekly-report";

const FILTERS = [
	"mine",
	"all",
	"REQUESTED",
	"IN_PROGRESS",
	"DELIVERED",
	"none",
] as const;
type Filter = (typeof FILTERS)[number];

const searchSchema = z.object({
	filter: z.enum(FILTERS).optional(),
	// The open request — a link sales can send the designer straight to.
	training: z.string().optional(),
});

type FichesSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/admin/fiches")({
	validateSearch: (search: Record<string, unknown>): FichesSearch => {
		const parsed = searchSchema.safeParse(search);
		return parsed.success ? parsed.data : {};
	},
	beforeLoad: async () => {
		const session = await getSession();
		if (!session?.user) {
			throw redirect({ to: "/admin/login" });
		}
		if (!canViewFiches(session.user.role)) {
			throw redirect({ to: homeRouteFor(session.user.role) });
		}
	},
	component: FichesPage,
});

const CARD = "rounded-2xl border border-line bg-white shadow-card";

const STATUS_STYLES: Record<FicheStatus | "none", string> = {
	REQUESTED: "bg-amber-50 text-amber-800",
	IN_PROGRESS: "bg-teal-50 text-teal-600",
	DELIVERED: "bg-emerald-50 text-emerald-700",
	none: "bg-canvas text-muted",
};

function StatusBadge({ status }: { status: FicheStatus | null }) {
	return (
		<span
			className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_STYLES[status ?? "none"]}`}
		>
			{status ? FICHE_STATUS_LABELS[status] : "لا توجد بطاقة"}
		</span>
	);
}

function formatDateTime(value: string | Date) {
	return new Intl.DateTimeFormat("ar-DZ", {
		dateStyle: "medium",
		timeStyle: "short",
		timeZone: "Africa/Algiers",
	}).format(new Date(value));
}

function FichesPage() {
	const { trpc } = useRouteContext({ from: "__root__" });
	const { data: session } = authClient.useSession();
	const role = session?.user?.role;
	const navigate = useNavigate({ from: Route.fullPath });
	const search = Route.useSearch();
	// The designer opens on their queue; sales and admins on every training,
	// since a training with no sheet yet is theirs to start.
	const defaultFilter: Filter = isDesigner(role) ? "mine" : "all";
	const filter: Filter = search.filter ?? defaultFilter;
	const today = algiersToday();

	const query = useQuery(trpc.fiches.list.queryOptions());
	const rows = query.data ?? [];

	const matches: Record<Filter, (row: (typeof rows)[number]) => boolean> = {
		mine: (row) => (row.fiche ? waitsOn(role, row.fiche.status) : false),
		all: () => true,
		REQUESTED: (row) => row.fiche?.status === "REQUESTED",
		IN_PROGRESS: (row) => row.fiche?.status === "IN_PROGRESS",
		DELIVERED: (row) => row.fiche?.status === "DELIVERED",
		none: (row) => !row.fiche,
	};

	const tabs: TabItem<Filter>[] = [
		{ key: "mine", label: "بانتظارك", count: rows.filter(matches.mine).length },
		{ key: "all", label: "الكل", count: rows.length },
		{
			key: "REQUESTED",
			label: FICHE_STATUS_LABELS.REQUESTED,
			count: rows.filter(matches.REQUESTED).length,
		},
		{
			key: "IN_PROGRESS",
			label: FICHE_STATUS_LABELS.IN_PROGRESS,
			count: rows.filter(matches.IN_PROGRESS).length,
		},
		{
			key: "DELIVERED",
			label: FICHE_STATUS_LABELS.DELIVERED,
			count: rows.filter(matches.DELIVERED).length,
		},
		{
			key: "none",
			label: "بدون بطاقة",
			count: rows.filter(matches.none).length,
		},
	];

	// Most pressing first: whatever is due (or starts) soonest, then by name.
	const urgency = (row: (typeof rows)[number]) => {
		const due = row.fiche?.dueDate ? isoDay(row.fiche.dueDate) : null;
		const start = row.nextEdition ? isoDay(row.nextEdition.startDate) : null;
		return [due, start].filter(Boolean).sort()[0] ?? "9999-12-31";
	};
	const visible = rows
		.filter(matches[filter])
		.sort(
			(a, b) =>
				urgency(a).localeCompare(urgency(b)) || a.name.localeCompare(b.name),
		);

	function setSearch(next: FichesSearch) {
		void navigate({ search: next, replace: true });
	}

	const intro = canEditFicheDesign(role)
		? canEditFicheSheet(role)
			? "البطاقة التقنية لكل تدريب والتصميم المنجز منها للتواصل الاجتماعي."
			: "حوّل البطاقة التقنية لكل تدريب إلى تصميم للتواصل الاجتماعي وارفعه هنا."
		: "ارفع البطاقة التقنية لكل تدريب ليُنجز المصمّم تصميمها للتواصل الاجتماعي.";

	return (
		<div className="flex flex-col gap-5">
			<div>
				<h1 className="text-2xl font-bold text-navy">البطاقات التقنية</h1>
				<p className="text-sm text-muted">{intro}</p>
			</div>

			<TabBar
				tabs={tabs}
				value={filter}
				onChange={(key) =>
					setSearch({
						...search,
						filter: key === defaultFilter ? undefined : key,
					})
				}
			/>

			{query.isLoading ? (
				<div className={`flex justify-center py-16 ${CARD}`}>
					<Loader2 className="h-6 w-6 animate-spin text-muted" />
				</div>
			) : query.isError ? (
				<p className={`px-6 py-10 text-center text-sm text-red-600 ${CARD}`}>
					{query.error.message}
				</p>
			) : visible.length === 0 ? (
				<p className={`px-6 py-10 text-center text-sm text-muted ${CARD}`}>
					{filter === "mine"
						? "لا شيء بانتظارك الآن."
						: "لا توجد تدريبات في هذه القائمة."}
				</p>
			) : (
				<ul className={`divide-y divide-line ${CARD}`}>
					{visible.map((row) => {
						const due = row.fiche?.dueDate ? isoDay(row.fiche.dueDate) : null;
						const late =
							due !== null && due < today && row.fiche?.status !== "DELIVERED";
						return (
							<li key={row.id}>
								<button
									type="button"
									onClick={() => setSearch({ ...search, training: row.id })}
									className="flex w-full flex-col gap-2 px-5 py-4 text-start transition-colors hover:bg-canvas/60 sm:flex-row sm:items-center sm:gap-4"
								>
									<div className="flex min-w-0 flex-1 flex-col gap-1">
										<div className="flex flex-wrap items-center gap-2">
											<span className="font-bold text-navy">{row.name}</span>
											<StatusBadge status={row.fiche?.status ?? null} />
										</div>
										<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
											{row.fiche ? (
												<>
													<span className="inline-flex items-center gap-1">
														<FileText className="h-3.5 w-3.5" />
														{row.fiche.sheets} بطاقة
													</span>
													<span className="inline-flex items-center gap-1">
														<FileImage className="h-3.5 w-3.5" />
														{row.fiche.designs} تصميم
													</span>
												</>
											) : null}
											{row.nextEdition ? (
												<span>
													{editionLabel(row.nextEdition.editionNumber)} تبدأ{" "}
													{dayLabel(isoDay(row.nextEdition.startDate))}
												</span>
											) : null}
										</div>
									</div>
									{due ? (
										<span
											className={`inline-flex shrink-0 items-center gap-1.5 text-xs font-bold ${late ? "text-red-600" : "text-navy"}`}
										>
											<CalendarClock className="h-4 w-4" />
											{late ? "متأخر — " : "مطلوب قبل "}
											{dayLabel(due)}
										</span>
									) : null}
								</button>
							</li>
						);
					})}
				</ul>
			)}

			{search.training ? (
				<FicheDialog
					trainingId={search.training}
					role={role}
					onClose={() => setSearch({ ...search, training: undefined })}
				/>
			) : null}
		</div>
	);
}

function FicheDialog({
	trainingId,
	role,
	onClose,
}: {
	trainingId: string;
	role: string | null | undefined;
	onClose: () => void;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const queryClient = useQueryClient();
	const query = useQuery(trpc.fiches.get.queryOptions({ trainingId }));
	const [error, setError] = useState<string | null>(null);

	function refresh() {
		void queryClient.invalidateQueries({
			queryKey: trpc.fiches.get.queryKey({ trainingId }),
		});
		void queryClient.invalidateQueries({
			queryKey: trpc.fiches.list.queryKey(),
		});
	}

	const statusMutation = useMutation({
		...trpc.fiches.setStatus.mutationOptions(),
		onSuccess: refresh,
		onError: (err) => setError(err.message),
	});

	const training = query.data;
	const fiche = training?.fiche ?? null;
	const sheets = fiche?.files.filter((f) => f.kind === "SHEET") ?? [];
	const designs = fiche?.files.filter((f) => f.kind === "DESIGN") ?? [];
	const actions = fiche
		? ficheActions(role, fiche.status, {
				sheets: sheets.length,
				designs: designs.length,
			})
		: [];

	return (
		<Modal
			title={training?.name ?? "البطاقة التقنية"}
			subtitle={
				fiche ? (
					<span className="flex flex-wrap items-center gap-2">
						<StatusBadge status={fiche.status} />
						{fiche.requestedBy ? (
							<span>طلبها {fiche.requestedBy.name}</span>
						) : null}
						{fiche.deliveredAt ? (
							<span>· سُلّم {formatDateTime(fiche.deliveredAt)}</span>
						) : null}
					</span>
				) : (
					"لم تُرفع البطاقة التقنية بعد"
				)
			}
			width="xl"
			onClose={onClose}
			footer={
				<div className="flex flex-wrap items-center justify-between gap-2">
					{error ? (
						<p className="text-sm font-semibold text-red-600">{error}</p>
					) : (
						<span />
					)}
					<div className="flex flex-wrap gap-2">
						<GhostButton onClick={onClose}>إغلاق</GhostButton>
						{actions.map((action) => (
							<PrimaryButton
								key={action.to}
								disabled={statusMutation.isPending}
								onClick={() => {
									setError(null);
									statusMutation.mutate({ trainingId, status: action.to });
								}}
							>
								{statusMutation.isPending ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<Check className="h-4 w-4" />
								)}
								{action.label}
							</PrimaryButton>
						))}
					</div>
				</div>
			}
		>
			{query.isLoading ? (
				<div className="flex justify-center py-10">
					<Loader2 className="h-6 w-6 animate-spin text-muted" />
				</div>
			) : query.isError || !training ? (
				<p className="py-10 text-center text-sm text-red-600">
					{query.error?.message ?? "التدريب غير موجود"}
				</p>
			) : (
				<div className="grid gap-6 lg:grid-cols-2">
					<section className="flex flex-col gap-4">
						<h3 className="flex items-center gap-2 font-bold text-navy">
							<FileText className="h-4.5 w-4.5" />
							البطاقة التقنية
						</h3>
						<FileList
							files={sheets}
							canEdit={canEditFicheSheet(role)}
							onChanged={refresh}
							empty="لا توجد بطاقة تقنية بعد."
						/>
						{canEditFicheSheet(role) ? (
							<UploadButton
								trainingId={trainingId}
								kind="SHEET"
								label={sheets.length ? "رفع نسخة جديدة" : "رفع البطاقة التقنية"}
								onUploaded={refresh}
							/>
						) : null}
						<BriefEditor
							trainingId={trainingId}
							brief={fiche?.brief ?? null}
							dueDate={fiche?.dueDate ? isoDay(fiche.dueDate) : null}
							canEdit={canEditFicheSheet(role)}
							onSaved={refresh}
						/>
					</section>

					<section className="flex flex-col gap-4 lg:border-s lg:border-line lg:ps-6">
						<h3 className="flex items-center gap-2 font-bold text-navy">
							<FileImage className="h-4.5 w-4.5" />
							التصميم
						</h3>
						{fiche ? (
							<>
								<FileList
									files={designs}
									canEdit={canEditFicheDesign(role)}
									onChanged={refresh}
									empty="لم يُرفع أي تصميم بعد."
									thumbnails
								/>
								{canEditFicheDesign(role) ? (
									<UploadButton
										trainingId={trainingId}
										kind="DESIGN"
										label="رفع تصميم"
										onUploaded={refresh}
									/>
								) : null}
								<NoteEditor
									trainingId={trainingId}
									note={fiche.designNote}
									canEdit={canEditFicheDesign(role)}
									onSaved={refresh}
								/>
							</>
						) : (
							<p className="rounded-xl bg-canvas px-4 py-6 text-center text-sm text-muted">
								يظهر التصميم هنا بعد أن يرفع فريق المبيعات البطاقة التقنية.
							</p>
						)}
					</section>
				</div>
			)}
		</Modal>
	);
}

interface FicheFileRow {
	id: string;
	kind: FicheFileKind;
	name: string;
	contentType: string;
	size: number;
	createdAt: string | Date;
	uploadedBy: { name: string } | null;
}

function fileUrl(id: string, download = false) {
	return `/api/admin/fiche-file/${id}${download ? "?download=1" : ""}`;
}

function FileList({
	files,
	canEdit,
	onChanged,
	empty,
	thumbnails = false,
}: {
	files: FicheFileRow[];
	canEdit: boolean;
	onChanged: () => void;
	empty: string;
	thumbnails?: boolean;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const [confirming, setConfirming] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const removeMutation = useMutation({
		...trpc.fiches.removeFile.mutationOptions(),
		onSuccess: () => {
			setConfirming(null);
			onChanged();
		},
		onError: (err) => setError(err.message),
	});

	if (!files.length) {
		return (
			<p className="rounded-xl bg-canvas px-4 py-6 text-center text-sm text-muted">
				{empty}
			</p>
		);
	}

	return (
		<div className="flex flex-col gap-2">
			<ul className="flex flex-col gap-2">
				{files.map((file) => (
					<li
						key={file.id}
						className="flex flex-col gap-3 rounded-xl border border-line p-3"
					>
						{thumbnails && file.contentType.startsWith("image/") ? (
							<a
								href={fileUrl(file.id)}
								target="_blank"
								rel="noreferrer"
								className="block overflow-hidden rounded-lg bg-canvas"
							>
								<img
									src={fileUrl(file.id)}
									alt={file.name}
									loading="lazy"
									className="max-h-64 w-full object-contain"
								/>
							</a>
						) : null}
						<div className="flex items-center gap-3">
							<div className="min-w-0 flex-1">
								<p className="truncate text-sm font-bold text-navy" dir="auto">
									{file.name}
								</p>
								{/* <bdi> per part: "329 KB" and the date are LTR runs that
								    the RTL paragraph would otherwise splice together. */}
								<p className="text-xs text-muted">
									<bdi>{formatBytes(file.size)}</bdi> ·{" "}
									<bdi>{formatDateTime(file.createdAt)}</bdi>
									{file.uploadedBy ? (
										<>
											{" "}
											· <bdi>{file.uploadedBy.name}</bdi>
										</>
									) : null}
								</p>
							</div>
							{isPreviewable(file.contentType) ? (
								<a
									href={fileUrl(file.id)}
									target="_blank"
									rel="noreferrer"
									className="rounded-full p-2 text-muted transition-colors hover:bg-canvas hover:text-navy"
									aria-label="فتح"
									title="فتح"
								>
									<ExternalLink className="h-4 w-4" />
								</a>
							) : null}
							<a
								href={fileUrl(file.id, true)}
								className="rounded-full p-2 text-muted transition-colors hover:bg-canvas hover:text-navy"
								aria-label="تحميل"
								title="تحميل"
							>
								<Download className="h-4 w-4" />
							</a>
							{canEdit ? (
								confirming === file.id ? (
									<span className="flex items-center gap-1">
										<button
											type="button"
											disabled={removeMutation.isPending}
											onClick={() => {
												setError(null);
												removeMutation.mutate({ fileId: file.id });
											}}
											className="rounded-full bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100 disabled:opacity-50"
										>
											{removeMutation.isPending ? "…" : "تأكيد الحذف"}
										</button>
										<button
											type="button"
											onClick={() => setConfirming(null)}
											className="rounded-full px-2 py-1.5 text-xs font-bold text-muted hover:text-navy"
										>
											إلغاء
										</button>
									</span>
								) : (
									<button
										type="button"
										onClick={() => setConfirming(file.id)}
										className="rounded-full p-2 text-muted transition-colors hover:bg-red-50 hover:text-red-600"
										aria-label="حذف"
										title="حذف"
									>
										<Trash2 className="h-4 w-4" />
									</button>
								)
							) : null}
						</div>
					</li>
				))}
			</ul>
			{error ? (
				<p className="text-xs font-semibold text-red-600">{error}</p>
			) : null}
		</div>
	);
}

function UploadButton({
	trainingId,
	kind,
	label,
	onUploaded,
}: {
	trainingId: string;
	kind: FicheFileKind;
	label: string;
	onUploaded: () => void;
}) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [busy, setBusy] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	async function upload(files: File[]) {
		setError(null);
		// Check them all first, so one bad file doesn't leave half a batch.
		for (const file of files) {
			const invalid = validateFicheUpload(kind, file);
			if (invalid) {
				setError(`${file.name}: ${invalid}`);
				return;
			}
		}
		try {
			for (const file of files) {
				setBusy(file.name);
				const body = new FormData();
				body.append("file", file);
				body.append("kind", kind);
				body.append("trainingId", trainingId);
				const response = await fetch("/api/admin/fiche-upload", {
					method: "POST",
					body,
				});
				const payload = (await response.json().catch(() => null)) as {
					error?: string;
				} | null;
				if (!response.ok) {
					setError(payload?.error ?? "تعذّر رفع الملف. حاول مجدداً.");
					return;
				}
				onUploaded();
			}
		} catch {
			setError("تعذّر الاتصال بالخادم. حاول مجدداً.");
		} finally {
			setBusy(null);
			if (inputRef.current) inputRef.current.value = "";
		}
	}

	return (
		<div className="flex flex-col gap-1.5">
			<input
				ref={inputRef}
				type="file"
				multiple
				accept={FICHE_ACCEPT_ATTR[kind]}
				className="hidden"
				onChange={(e) => {
					const files = Array.from(e.target.files ?? []);
					if (files.length) void upload(files);
				}}
			/>
			<GhostButton
				disabled={busy !== null}
				onClick={() => inputRef.current?.click()}
				className="self-start"
			>
				{busy ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<Upload className="h-4 w-4" />
				)}
				{busy ? `جارٍ رفع ${busy}…` : label}
			</GhostButton>
			{error ? (
				<p className="text-xs font-semibold text-red-600">{error}</p>
			) : null}
		</div>
	);
}

function BriefEditor({
	trainingId,
	brief,
	dueDate,
	canEdit,
	onSaved,
}: {
	trainingId: string;
	brief: string | null;
	dueDate: string | null;
	canEdit: boolean;
	onSaved: () => void;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const [text, setText] = useState(brief ?? "");
	const [due, setDue] = useState(dueDate ?? "");
	const [saved, setSaved] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Pick up what the server holds whenever it changes under us.
	useEffect(() => {
		setText(brief ?? "");
		setDue(dueDate ?? "");
	}, [brief, dueDate]);

	const mutation = useMutation({
		...trpc.fiches.saveBrief.mutationOptions(),
		onSuccess: () => {
			setSaved(true);
			onSaved();
		},
		onError: (err) => setError(err.message),
	});

	if (!canEdit) {
		return (
			<div className="flex flex-col gap-2 rounded-xl bg-canvas p-4">
				<p className="text-xs font-bold text-muted">توجيهات للمصمّم</p>
				<p className="whitespace-pre-line text-sm text-navy" dir="auto">
					{brief || "لا توجد توجيهات."}
				</p>
				{dueDate ? (
					<p className="inline-flex items-center gap-1.5 text-xs font-bold text-navy">
						<CalendarClock className="h-4 w-4" />
						مطلوب قبل {dayLabel(dueDate)}
					</p>
				) : null}
			</div>
		);
	}

	const dirty = text !== (brief ?? "") || due !== (dueDate ?? "");

	return (
		<div className="flex flex-col gap-3 rounded-xl bg-canvas p-4">
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				توجيهات للمصمّم
				<textarea
					value={text}
					onChange={(e) => {
						setText(e.target.value);
						setSaved(false);
					}}
					rows={4}
					maxLength={4000}
					dir="auto"
					placeholder="المقاسات المطلوبة (منشور، ستوري…)، الرسائل الأساسية، أي ملاحظات."
					className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm font-normal text-navy outline-none focus:border-teal"
				/>
			</label>
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				مطلوب قبل
				<input
					type="date"
					value={due}
					onChange={(e) => {
						setDue(e.target.value);
						setSaved(false);
					}}
					className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm font-normal text-navy outline-none focus:border-teal sm:w-48"
				/>
			</label>
			<div className="flex items-center gap-3">
				<GhostButton
					disabled={!dirty || mutation.isPending}
					onClick={() => {
						setError(null);
						mutation.mutate({
							trainingId,
							brief: text.trim() || null,
							dueDate: due || null,
						});
					}}
				>
					{mutation.isPending ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : null}
					حفظ التوجيهات
				</GhostButton>
				{saved && !dirty ? (
					<span className="text-xs font-bold text-emerald-700">تم الحفظ</span>
				) : null}
				{error ? (
					<span className="text-xs font-semibold text-red-600">{error}</span>
				) : null}
			</div>
		</div>
	);
}

function NoteEditor({
	trainingId,
	note,
	canEdit,
	onSaved,
}: {
	trainingId: string;
	note: string | null;
	canEdit: boolean;
	onSaved: () => void;
}) {
	const { trpc } = useRouteContext({ from: "__root__" });
	const [text, setText] = useState(note ?? "");
	const [saved, setSaved] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setText(note ?? "");
	}, [note]);

	const mutation = useMutation({
		...trpc.fiches.saveDesignNote.mutationOptions(),
		onSuccess: () => {
			setSaved(true);
			onSaved();
		},
		onError: (err) => setError(err.message),
	});

	if (!canEdit) {
		return note ? (
			<div className="flex flex-col gap-2 rounded-xl bg-canvas p-4">
				<p className="text-xs font-bold text-muted">ملاحظة المصمّم</p>
				<p className="whitespace-pre-line text-sm text-navy" dir="auto">
					{note}
				</p>
			</div>
		) : null;
	}

	const dirty = text !== (note ?? "");

	return (
		<div className="flex flex-col gap-3 rounded-xl bg-canvas p-4">
			<label className="flex flex-col gap-1.5 text-xs font-bold text-muted">
				ملاحظة لفريق المبيعات
				<textarea
					value={text}
					onChange={(e) => {
						setText(e.target.value);
						setSaved(false);
					}}
					rows={3}
					maxLength={4000}
					dir="auto"
					placeholder="ما الذي سُلّم، وأي شيء ينقص من البطاقة."
					className="w-full rounded-xl border border-line bg-white px-3 py-2 text-sm font-normal text-navy outline-none focus:border-teal"
				/>
			</label>
			<div className="flex items-center gap-3">
				<GhostButton
					disabled={!dirty || mutation.isPending}
					onClick={() => {
						setError(null);
						mutation.mutate({ trainingId, designNote: text.trim() || null });
					}}
				>
					{mutation.isPending ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : null}
					حفظ الملاحظة
				</GhostButton>
				{saved && !dirty ? (
					<span className="text-xs font-bold text-emerald-700">تم الحفظ</span>
				) : null}
				{error ? (
					<span className="text-xs font-semibold text-red-600">{error}</span>
				) : null}
			</div>
		</div>
	);
}
